/**
 * One instructor's selectable card in the Step 0 carousel — [BUT-843, ours].
 *
 * Figma `14549:733252` (unselected) / `14549:733260` (selected) /
 * `14549:733337` (selected check badge) — raw Figma output, adapted not
 * copied. No specialty subtitle and no rate / "/hour" — the
 * instructor payload carries neither.
 *
 * Selection styling is `data-selected` + CSS, not a conditional className
 * (Native Web First) — the only React state involved is owned by the parent.
 *
 * @module pages/lessons/steps/components/InstructorCard
 */

import { CheckmarkIcon } from '@butter/ui/icons';
import { InstructorIdentity } from '@butter/ui/domains/lessons';
import { lessonInstructorName } from '@butter/library/lessons';
import type { ClubInstructor } from '~/services/internalApi/lessons';

export interface InstructorCardProps {
  instructor: ClubInstructor;
  selected: boolean;
  /** Pack holders only — this instructor is in `pack.allowedInstructors`. */
  included?: boolean;
  onSelect: (instructor: ClubInstructor) => void;
}

export function InstructorCard({ instructor, selected, included = false, onSelect }: InstructorCardProps) {
  const name = lessonInstructorName(instructor);

  // Phone: full-width row. Desktop: a quarter-width column (the `<li>` sizing
  // lives on SnapCarousel's `itemClassName`); past four cards the row scrolls.
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Book a lesson with ${name}`}
      data-selected={selected}
      data-testid="lesson-instructor-card"
      data-instructor-id={instructor.id}
      onClick={() => onSelect(instructor)}
      className="relative flex w-full items-center gap-2.5 rounded-20 border border-butter-gray-100 bg-bg-tertiary p-4.5 text-left shadow-sm text-interactive:border-theme-300 data-[selected=true]:border-theme-600 data-[selected=true]:bg-gradient-to-b data-[selected=true]:from-white data-[selected=true]:to-theme-25 md:flex-col md:gap-1 md:rounded-3xl md:p-6 md:text-center"
    >
      <InstructorIdentity name={name} image={instructor.image} variant="card" />

      <span className="ml-auto flex items-center gap-2 md:contents">
        {included && (
          <span
            className="rounded-full bg-theme-50 px-3 py-1 text-sm font-semibold text-theme-700 md:px-2 md:py-0 md:font-medium md:leading-4"
            data-testid="lesson-instructor-included-badge"
          >
            Included
          </span>
        )}

        {selected && (
          <span
            aria-hidden
            data-testid="lesson-instructor-selected-badge"
            className="size-6 inline-flex items-center justify-center rounded-full bg-fg-primary text-white md:absolute md:right-6 md:top-7"
          >
            <CheckmarkIcon className="size-3" />
          </span>
        )}
      </span>
    </button>
  );
}
